import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, ArrowRight, User, Users, ImageOff } from "lucide-react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

function whatsappLink(serviceName) {
  const msg = encodeURIComponent(`Hi Joyce, I'm interested in ${serviceName}. Could we discuss availability?`);
  return `https://wa.me/6597946649?text=${msg}`;
}

function ServiceCard({ svc, i, flipped }) {
  const imageUrl = svc.image_url;

  return (
    <motion.div
      initial="hidden" whileInView="visible" viewport={{ once: true }}
      variants={fadeUp} custom={i * 0.3}
    >
      <div className="group bg-white border border-border rounded-3xl overflow-hidden hover:shadow-lift transition-all duration-300">
        <div className={`grid grid-cols-1 md:grid-cols-12 gap-0 ${flipped ? "md:direction-rtl" : ""}`}>
          <div className={`${flipped ? "md:order-2" : ""} md:col-span-4 h-64 md:h-auto overflow-hidden img-zoom`}>
            {imageUrl ? (
              <img src={imageUrl} alt={svc.name} className="w-full h-full object-cover min-h-[280px]" />
            ) : (
              <div className="w-full h-full min-h-[280px] bg-champagne-dark flex items-center justify-center">
                <ImageOff className="w-10 h-10 text-soft-grey" />
              </div>
            )}
          </div>
          <div className={`${flipped ? "md:order-1" : ""} md:col-span-8 p-8 md:p-10 flex flex-col`}>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-charcoal mb-4">{svc.name}</h3>
            <p className="font-body text-charcoal-light leading-relaxed mb-6 flex-1">{svc.description}</p>
            <div className="flex items-center justify-between pt-5 border-t border-border">
              <div className="flex items-center gap-4 font-body text-sm text-charcoal-light">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {svc.duration_minutes} min
                </span>
                {svc.price > 0 && (
                  <span className="font-heading text-xl font-bold text-navy">
                    {svc.currency} {svc.price}
                  </span>
                )}
              </div>
              <a
                href={whatsappLink(svc.name)}
                target="_blank" rel="noopener noreferrer"
                data-testid={`book-${svc.slug}-btn`}
                className="inline-flex items-center gap-2 bg-navy text-white px-6 py-3 rounded-full font-body font-semibold text-sm hover:bg-navy-light transition-all hover:scale-105 shadow-soft"
              >
                Book Now <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function WorkWithMePage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(console.error);
  }, []);

  const individuals = services.filter((s) => s.audience === "individual");
  const families = services.filter((s) => s.audience === "family");

  return (
    <div className="min-h-screen bg-champagne" data-testid="services-page">
      <Navbar />
      <section className="pt-32 pb-8 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-charcoal"
          >
            Work With Me
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-body text-charcoal-light mt-4 max-w-2xl text-lg leading-relaxed"
          >
            Each session is tailored to your unique needs. Reach out to Joyce on WhatsApp
            to discuss availability and book your session.
          </motion.p>
        </div>
      </section>

      {/* For Individuals */}
      {individuals.length > 0 && (
        <section className="py-12 px-6" data-testid="individual-services">
          <div className="max-w-6xl mx-auto">
            <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="font-heading text-2xl font-semibold text-charcoal mb-8 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-navy" />
              </div>
              For Individuals
            </motion.h2>
            <div className="space-y-8">
              {individuals.map((svc, i) => <ServiceCard key={svc.id} svc={svc} i={i} flipped={i % 2 === 1} />)}
            </div>
          </div>
        </section>
      )}

      {/* For Families & Parents */}
      {families.length > 0 && (
        <section className="py-12 px-6" data-testid="family-services">
          <div className="max-w-6xl mx-auto">
            <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="font-heading text-2xl font-semibold text-charcoal mb-8 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-navy" />
              </div>
              For Families & Parents
            </motion.h2>
            <div className="space-y-8">
              {families.map((svc, i) => <ServiceCard key={svc.id} svc={svc} i={i} flipped={i % 2 === 1} />)}
            </div>
          </div>
        </section>
      )}

      {/* WhatsApp CTA */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-body text-charcoal-light mb-4 text-lg">Not sure which service is right for you?</p>
          <a
            href="https://wa.me/6597946649"
            target="_blank" rel="noopener noreferrer"
            data-testid="services-whatsapp-btn"
            className="inline-flex items-center gap-2 border-2 border-navy text-navy px-8 py-3 rounded-full font-body font-semibold hover:bg-navy/5 transition-all"
          >
            Talk to Joyce on WhatsApp
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
